import React, { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Reviewer decline-batch modal — requires a reason. */
export default function DeclineBatchModal({ batch, onClose, onDeclined }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const r = reason.trim();
    if (!r) {
      toast.error("Please give a reason");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/batches/${batch.id}/decline`, { reason: r });
      toast.success("Batch declined");
      onDeclined();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div data-testid="decline-modal" className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Decline this batch?
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-slate-500 mb-4">
          Declining returns <strong>{batch.name}</strong> to the unassigned pool. Any drafts you
          saved will be wiped.
        </p>

        <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
          Reason
        </label>
        <textarea
          data-testid="decline-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="e.g. Outside my domain — should be assigned to Anatomy reviewer"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        />

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button
            data-testid="decline-submit-btn"
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold"
          >
            {busy ? "Declining…" : "Decline Batch"}
          </button>
        </div>
      </div>
    </div>
  );
}
